import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { useNotification } from './NotificationProvider';
import { FiltersStatus } from './SharedFiltersProvider';
import { FinTechOpsMessages } from '../../FinTechOpsConstants';
import { RISK_REMEDIATOR_API_ERROR, RISK_REMEDIATOR_INITIAL_DATA, L8_MANAGER_DETAILS } from '../../risk-remediator/constants';
import { RISK_TYPE_FLAG } from '../../risk-remediator/constants';
import { RemediatorManagerList, RemediatorPayload, RiskRemediatorData } from '../../risk-remediator/RiskRemediatorTypes';
import { useRiskRemediatorData } from '../hooks/useRiskRemediatorData';

export interface RiskRemediatorDashboardData {
  totalRisks: number;
  openRisks: number;
  autoRemediated: number;
  devRemediated: number;
  remediated: number;
  autoRemediationRate: number;
}

const EMPTY_RR_DASHBOARD: RiskRemediatorDashboardData = {
  totalRisks: 0,
  openRisks: 0,
  autoRemediated: 0,
  devRemediated: 0,
  remediated: 0,
  autoRemediationRate: 0
};

type RiskArray = Record<string, string>[];
interface ParsedRisks {
  open: RiskArray;
  remediated: RiskArray;
  devRemediated: RiskArray;
}
const EMPTY_PARSED: ParsedRisks = { open: [], remediated: [], devRemediated: [] };

/** Parse the details JSON and extract risk arrays filtered by the default manager. */
const parseRiskDetails = (details: string | undefined, manager: string): ParsedRisks => {
  if (!details || typeof details !== 'string') return EMPTY_PARSED;
  try {
    const parsed = JSON.parse(details);
    const filter = (arr: RiskArray) =>
      (arr || []).filter(
        (r) =>
          r.leadership_rollup
            ?.split(',')
            .map((s) => s.trim())
            .includes(manager) || r.owner_alias === manager
      );
    return {
      open: filter(parsed[RISK_TYPE_FLAG.OPEN]),
      remediated: filter(parsed[RISK_TYPE_FLAG['AUTO-REMEDIATED']]),
      devRemediated: filter(parsed[RISK_TYPE_FLAG['DEV-REMEDIATED']])
    };
  } catch (error: any) {
    logger.error('Failed to parse risk details', error);
    return EMPTY_PARSED;
  }
};

/** Compute dashboard metrics from risk arrays. */
export const computeRRDashboard = (risks: ParsedRisks): RiskRemediatorDashboardData => {
  const { open, remediated, devRemediated } = risks;
  const total = open.length + remediated.length + devRemediated.length;
  const rem = remediated.length + devRemediated.length;
  const rate = total > 0 ? Number(((remediated.length / total) * 100).toFixed(1)) : 0;
  return {
    totalRisks: total,
    openRisks: open.length,
    autoRemediated: remediated.length,
    devRemediated: devRemediated.length,
    remediated: rem,
    autoRemediationRate: rate
  };
};

export interface RiskRemediatorContextValue {
  finTechOpsRiskRemediatorStatus: FiltersStatus;
  setFinTechOpsRiskRemediatorStatus: (s: FiltersStatus) => void;
  finTechOpsRiskRemediatorData: RiskRemediatorData;
  finTechOpsRemediatorManagerData: RemediatorManagerList;
  finTechOpsRemediatorDirectsData: RemediatorPayload;
  finTechOpsRemediatorAsrData: RemediatorPayload;
  finTechOpsRemediatorVpcData: RemediatorPayload;
  finTechOpsRiskRemediatorDashboardData: RiskRemediatorDashboardData;
  /** Recompute dashboard data from filtered risk arrays. Provider owns the computation. */
  filterDashboardRisks: (filteredRisks: ParsedRisks) => void;
  /** Parsed risk arrays (unfiltered, scoped to default manager) for filter recomputation. */
  finTechOpsRiskRemediatorParsedRisks: ParsedRisks;
}

const EMPTY_PAYLOAD = { details: '' } as RemediatorPayload;

const defaultValue: RiskRemediatorContextValue = {
  finTechOpsRiskRemediatorStatus: 'loading',
  setFinTechOpsRiskRemediatorStatus: () => {},
  finTechOpsRiskRemediatorData: RISK_REMEDIATOR_INITIAL_DATA as RiskRemediatorData,
  finTechOpsRemediatorManagerData: [],
  finTechOpsRemediatorDirectsData: EMPTY_PAYLOAD,
  finTechOpsRemediatorAsrData: EMPTY_PAYLOAD,
  finTechOpsRemediatorVpcData: EMPTY_PAYLOAD,
  finTechOpsRiskRemediatorDashboardData: EMPTY_RR_DASHBOARD,
  filterDashboardRisks: () => {},
  finTechOpsRiskRemediatorParsedRisks: EMPTY_PARSED
};

const RiskRemediatorContext = createContext<RiskRemediatorContextValue>(defaultValue);
export const useRiskRemediator = () => useContext(RiskRemediatorContext);

interface Props {
  children: React.ReactNode;
}

export const RiskRemediatorProvider = ({ children }: Props) => {
  const { data, loading, error } = useRiskRemediatorData();
  const { displayNotification } = useNotification();
  const [risks, setRisks] = useState<RiskRemediatorData>(RISK_REMEDIATOR_INITIAL_DATA as RiskRemediatorData);
  const [managers, setManagers] = useState<RemediatorManagerList>([]);
  const [directs, setDirects] = useState<RemediatorPayload>(EMPTY_PAYLOAD);
  const [asr, setAsr] = useState<RemediatorPayload>(EMPTY_PAYLOAD);
  const [vpc, setVpc] = useState<RemediatorPayload>(EMPTY_PAYLOAD);
  const [status, setStatus] = useState<FiltersStatus>('loading');
  const [dashboardData, setDashboardData] = useState<RiskRemediatorDashboardData>(EMPTY_RR_DASHBOARD);
  const [parsedRisks, setParsedRisks] = useState<ParsedRisks>(EMPTY_PARSED);

  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      logger.error('Unable to load Risk Remediator data', error);
      setStatus('error');
      displayNotification('error', FinTechOpsMessages.APIError, RISK_REMEDIATOR_API_ERROR);
      return;
    }
    if (data) {
      setRisks(data.risks);
      setManagers(data.managers);
      setDirects(data.directs);
      setAsr(data.asr);
      setVpc(data.vpc);

      // Parse risk details and compute initial dashboard data
      const details = (data.risks as Record<string, unknown>).details as string | undefined;
      const parsed = parseRiskDetails(details, L8_MANAGER_DETAILS.login);
      setParsedRisks(parsed);
      setDashboardData(computeRRDashboard(parsed));

      setStatus('finished');
    }
  }, [loading, error, data, displayNotification]);

  const setFinTechOpsRiskRemediatorStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const filterDashboardRisks = useCallback((filteredRisks: ParsedRisks) => setDashboardData(computeRRDashboard(filteredRisks)), []);

  const value = useMemo<RiskRemediatorContextValue>(
    () => ({
      finTechOpsRiskRemediatorStatus: status,
      setFinTechOpsRiskRemediatorStatus,
      finTechOpsRiskRemediatorData: risks,
      finTechOpsRemediatorManagerData: managers,
      finTechOpsRemediatorDirectsData: directs,
      finTechOpsRemediatorAsrData: asr,
      finTechOpsRemediatorVpcData: vpc,
      finTechOpsRiskRemediatorDashboardData: dashboardData,
      filterDashboardRisks,
      finTechOpsRiskRemediatorParsedRisks: parsedRisks
    }),
    [status, risks, managers, directs, asr, vpc, dashboardData, parsedRisks, setFinTechOpsRiskRemediatorStatus, filterDashboardRisks]
  );

  return <RiskRemediatorContext.Provider value={value}>{children}</RiskRemediatorContext.Provider>;
};
